import { useState, useRef, useCallback, useMemo, useEffect } from 'react'
import { useParams, useNavigate, Navigate } from 'react-router-dom'
import { toast } from 'sonner'
import { ArrowLeft, Check, History } from 'lucide-react'
import { MotionPlayIcon } from '@/components/icons'
import type * as monaco from 'monaco-editor'
import { useDebounce, useProject, useWorkflowDefinition, useCreateWorkflowDefinition, useUpdateWorkflowDefinition, useValidateWorkflowYaml, useWorkflowNodeTypes, usePrompts, useConnectors, useAgents, useAuth, useRoles, useSandboxImages } from '@/hooks'
import { Permissions } from '@/lib/permissions'
import { KEBAB_CASE_REGEX } from '@/types'
import type { WorkflowDefinitionCategory, LinkedEntityScope } from '@/types'
import type { MultiSelectOption } from '@/components/ui/multi-select'
import { Button } from '@/components/ui/button'
import {
 EditorFormFields,
 YamlMonacoEditor,
 BuildingBlocksPalette,
 YamlValidationPanel,
 validateYaml,
 YamlStructureTree,
 TriggerInputsDisplay,
 TriggerWorkflowDialog,
 WorkflowYamlSnapshotHistoryDialog,
} from '@/components/projects/workflows'

export function WorkflowDefinitionEditorPage() {
 const { projectId, definitionId } = useParams()
 const navigate = useNavigate()
 const { data: project } = useProject(projectId)
 const { hasPermission, isLoading: authLoading } = useAuth(projectId)
 const { data: roles } = useRoles()

 const [triggerDialogOpen, setTriggerDialogOpen] = useState(false)
 const [historyDialogOpen, setHistoryDialogOpen] = useState(false)

 const canEdit = hasPermission(Permissions.Project.Workflow.Create, projectId)
 const canToggle = hasPermission(Permissions.Project.Workflow.Edit, projectId)
 const canTrigger = hasPermission(Permissions.Project.Workflow.Trigger, projectId)

 const { data: nodeTypes } = useWorkflowNodeTypes(projectId!)
 const { data: prompts } = usePrompts(projectId!)
 const { data: connectors } = useConnectors(projectId!)
 const { data: agents } = useAgents(projectId!)
 const { data: sandboxImages } = useSandboxImages()

 const isNewMode = !definitionId || definitionId === 'new'
 const { data: existingDefinition, isLoading } = useWorkflowDefinition(
  projectId!,
  isNewMode ? '' : definitionId!,
 )

 const isSystem = existingDefinition?.isSystem ?? false
 const isReadOnly = isSystem || !canEdit

 const roleOptions: MultiSelectOption[] = useMemo(
  () => (roles ?? []).map((r) => ({ value: r.id, label: r.name })),
  [roles],
 )

 const [displayName, setDisplayName] = useState('')
 const [internalName, setInternalName] = useState('')
 const [description, setDescription] = useState('')
 const [version, setVersion] = useState('1.0.0')
 const [isActive, setIsActive] = useState(true)
 const [yamlContent, setYamlContent] = useState('')
 const [permittedRoleIds, setPermittedRoleIds] = useState<string[]>([])
 const [category, setCategory] = useState<WorkflowDefinitionCategory | undefined>(undefined)
 const [linkedEntityScope, setLinkedEntityScope] = useState<LinkedEntityScope | undefined>(undefined)
 const [attemptedSave, setAttemptedSave] = useState(false)
 const [initialized, setInitialized] = useState(isNewMode)

 useEffect(() => {
  if (existingDefinition && !initialized) {
   setDisplayName(existingDefinition.displayName)
   setInternalName(existingDefinition.name)
   setDescription(existingDefinition.description ?? '')
   setVersion(existingDefinition.version)
   setIsActive(existingDefinition.isActive)
   setYamlContent(existingDefinition.yamlContent)
   setPermittedRoleIds(existingDefinition.permittedRoleIds ?? [])
   setCategory(existingDefinition.category)
   setLinkedEntityScope(existingDefinition.linkedEntityScope)
   setInitialized(true)
  }
 }, [existingDefinition, initialized])

 const initialValues = useMemo(
  () => ({
   displayName: existingDefinition?.displayName ?? '',
   internalName: existingDefinition?.name ?? '',
   description: existingDefinition?.description ?? '',
   version: existingDefinition?.version ?? '1.0.0',
   isActive: existingDefinition?.isActive ?? true,
   yamlContent: existingDefinition?.yamlContent ?? '',
   permittedRoleIds: existingDefinition?.permittedRoleIds ?? [],
   category: existingDefinition?.category,
   linkedEntityScope: existingDefinition?.linkedEntityScope,
  }),
  [existingDefinition],
 )

 const isDirty = isNewMode
  ? !!(displayName || internalName || description || yamlContent || permittedRoleIds.length > 0)
  : displayName !== initialValues.displayName ||
   internalName !== initialValues.internalName ||
   description !== initialValues.description ||
   version !== initialValues.version ||
   isActive !== initialValues.isActive ||
   yamlContent !== initialValues.yamlContent ||
   category !== initialValues.category ||
   linkedEntityScope !== initialValues.linkedEntityScope ||
   JSON.stringify([...permittedRoleIds].sort()) !== JSON.stringify([...initialValues.permittedRoleIds].sort())

 const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null)
 const debouncedYaml = useDebounce(yamlContent, 300)

 const yamlValidation = useValidateWorkflowYaml(projectId!)

 useEffect(() => {
  if (debouncedYaml.trim() && validateYaml(debouncedYaml).isValid) {
   yamlValidation.mutate(debouncedYaml)
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when debouncedYaml changes
 }, [debouncedYaml])

 const hasYamlErrors = useMemo(() => {
  if (!validateYaml(debouncedYaml).isValid) return true
  if (yamlValidation.data && !yamlValidation.data.isValid) return true
  return false
 }, [debouncedYaml, yamlValidation.data])

 const createDefinition = useCreateWorkflowDefinition(projectId!)
 const updateDefinition = useUpdateWorkflowDefinition(projectId!)
 const isSaving = createDefinition.isPending || updateDefinition.isPending

 const handleInsertSnippet = useCallback((snippet: string) => {
  const editor = editorRef.current
  if (!editor) return

  const position = editor.getPosition()
  if (!position) return

  const model = editor.getModel()
  if (!model) return

  const currentLine = model.getLineContent(position.lineNumber)
  const leadingWhitespace = currentLine.match(/^\s*/)?.[0] ?? ''

  const indentedSnippet = snippet
   .split('\n')
   .map((line, i) => (i === 0 ? line : leadingWhitespace + line))
   .join('\n')

  editor.executeEdits('building-blocks', [
   {
    range: {
     startLineNumber: position.lineNumber,
     startColumn: position.column,
     endLineNumber: position.lineNumber,
     endColumn: position.column,
    },
    text: snippet.includes('\n') ? indentedSnippet + '\n' : indentedSnippet,
   },
  ])

  editor.focus()
 }, [])

 const handleGoToLine = useCallback((line: number) => {
  const editor = editorRef.current
  if (!editor) return
  editor.revealLineInCenter(line)
  editor.setPosition({ lineNumber: line, column: 1 })
  editor.focus()
 }, [])

 const hasFieldErrors = !displayName.trim() || !internalName.trim() || !KEBAB_CASE_REGEX.test(internalName.trim()) || !version.trim() || permittedRoleIds.length === 0

 const handleSave = async () => {
  setAttemptedSave(true)
  if (hasFieldErrors) return
  try {
   if (isNewMode) {
    await createDefinition.mutateAsync({
     name: internalName.trim(),
     displayName: displayName.trim(),
     description: description.trim() || undefined,
     version: version.trim(),
     yamlContent,
     isActive,
     permittedRoleIds,
     category,
     linkedEntityScope,
    })
    toast.success(`Created "${displayName.trim()}"`)
    navigate(`/projects/${projectId}/workflows/definitions`)
   } else {
    const result = await updateDefinition.mutateAsync({
     id: definitionId!,
     data: {
      name: internalName.trim(),
      displayName: displayName.trim(),
      description: description.trim() || undefined,
      version: version.trim(),
      yamlContent,
      isActive,
      permittedRoleIds,
      category,
      linkedEntityScope,
     },
    })
    setVersion(result.version)
    toast.success(`Saved "${displayName.trim()}"`)
   }
  } catch {
   toast.error(isNewMode ? 'Failed to create workflow definition' : 'Failed to save workflow definition')
  }
 }

 const handleTrigger = () => {
  setTriggerDialogOpen(true)
 }

 const handleBack = () => {
  navigate(`/projects/${projectId}/workflows/definitions`)
 }

 if (authLoading) {
  return null
 }

 if (!hasPermission(Permissions.Project.Workflow.View, projectId)) {
  return <Navigate to={`/projects/${projectId}/backlog`} replace />
 }

 if (!isNewMode && isLoading) {
  return (
   <div className="flex items-center justify-center h-[calc(100vh-4rem)]" role="status" aria-live="polite" data-testid="workflow-editor-loading">
    <div className="text-muted-foreground">Loading workflow definition...</div>
   </div>
  )
 }

 return (
  <div className="flex flex-col h-[calc(100vh-4rem)] -m-8">
   <div className="shrink-0 px-6 py-4">
    <div className="flex items-start justify-between">
     <div>
      <p className="text-sm text-primary font-medium">
       Project: {project?.name ?? 'Loading...'}
      </p>
      <div className="flex items-center gap-3">
       <Button variant="ghost" size="icon" onClick={handleBack} aria-label="Go back to definitions">
        <ArrowLeft className="h-5 w-5" />
       </Button>
       <h1 className="text-2xl font-bold leading-[1.3] tracking-[0.015em] text-foreground">
        {isNewMode ? 'New Definition' : existingDefinition?.displayName ?? 'Edit Definition'}
       </h1>
       {isSystem && (
        <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded">
         System (Read-Only)
        </span>
       )}
      </div>
     </div>
     <div className="flex items-center gap-2">
      {!isNewMode && existingDefinition && (
       <Button
        variant="outline"
        onClick={() => setHistoryDialogOpen(true)}
        className="h-10 rounded-lg px-4 gap-2"
       >
        <History className="h-5 w-5" />
        Version History
       </Button>
      )}
      {canEdit && (
       <Button
        onClick={handleSave}
        disabled={!isDirty || isReadOnly || isSaving || hasYamlErrors}
        className="h-10 rounded-lg px-4 gap-2"
        data-testid="save-definition-button"
       >
        <Check className="h-5 w-5" />
        {isSaving ? 'Saving...' : 'Save Definition'}
       </Button>
      )}
      {!isNewMode && canTrigger && (
       <Button
        onClick={handleTrigger}
        disabled={!existingDefinition?.isActive}
        className="h-10 min-w-10 rounded-lg px-4 py-2.5 gap-2.5"
        data-testid="trigger-definition-button"
       >
        <MotionPlayIcon className="h-5 w-5" />
        Trigger
       </Button>
      )}
     </div>
    </div>
   </div>

   <div className="flex-1 flex gap-4 min-h-0 px-4 pb-4">
    <div className="w-[300px] shrink-0">
     <BuildingBlocksPalette nodeTypes={nodeTypes} prompts={prompts} connectors={connectors} sandboxImages={sandboxImages} projectId={projectId} agents={agents} onInsertSnippet={handleInsertSnippet} />
    </div>

    <div className="flex-1 min-w-0 flex flex-col gap-2 bg-card rounded-2xl border border-border overflow-hidden">
     <div className="shrink-0 p-4">
      <EditorFormFields
       displayName={displayName}
       internalName={internalName}
       description={description}
       version={version}
       isActive={isActive}
       permittedRoleIds={permittedRoleIds}
       roleOptions={roleOptions}
       category={category}
       linkedEntityScope={linkedEntityScope}
       onDisplayNameChange={setDisplayName}
       onInternalNameChange={setInternalName}
       onDescriptionChange={setDescription}
       onVersionChange={setVersion}
       onIsActiveChange={setIsActive}
       onPermittedRoleIdsChange={setPermittedRoleIds}
       onCategoryChange={setCategory}
       onLinkedEntityScopeChange={setLinkedEntityScope}
       isReadOnly={isReadOnly}
       canChangeStatus={canToggle}
       attemptedSave={attemptedSave}
      />

      <p className="mt-3 text-xs text-muted-foreground">
       Workflow image is set via the top-level <code className="font-mono">sandboxImage:</code> field. Pick a slug from the Sandbox Images section in the palette. Required: runs cannot start without it.
      </p>
     </div>
     <div className="flex-1 min-h-0">
      <YamlMonacoEditor
       value={yamlContent}
       onChange={setYamlContent}
       editorRef={editorRef}
       readOnly={isReadOnly}
      />
     </div>
    </div>

    <div className="w-[300px] shrink-0 flex flex-col overflow-y-auto bg-card rounded-2xl border border-border">
     <div className="shrink-0 overflow-auto">
      <YamlValidationPanel
       yaml={debouncedYaml}
       backendValidation={yamlValidation.data}
       isValidating={yamlValidation.isPending}
       onGoToLine={handleGoToLine}
      />
     </div>
     <div className="flex-1 overflow-auto min-h-0">
      <YamlStructureTree yaml={debouncedYaml} />
      <TriggerInputsDisplay yaml={debouncedYaml} />
     </div>
    </div>
   </div>
   {existingDefinition && (
    <TriggerWorkflowDialog
     projectId={projectId!}
     definition={existingDefinition}
     open={triggerDialogOpen}
     onOpenChange={setTriggerDialogOpen}
    />
   )}
   {!isNewMode && (
    <WorkflowYamlSnapshotHistoryDialog
     open={historyDialogOpen}
     onOpenChange={setHistoryDialogOpen}
     projectId={projectId!}
     definitionId={definitionId!}
     currentYamlSnapshotId={existingDefinition?.currentYamlSnapshotId ?? null}
     currentEditorYaml={yamlContent}
    />
   )}
  </div>
 )
}
